import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetDeliveryZoneById } from '../../hooks/admin-get-delivery-zone-by-id.hook';
import { useUpdateDeliveryZone } from '../../hooks/update-delivery-zone.hook';
import { useArchiveDeliveryZone } from '../../hooks/archive-delivery-zone.hook';
import { useRestoreDeliveryZone } from '../../hooks/restore-delivery-zone.hook';
import { DeliveryZone } from '../../types/delivery-zone.type';
import { UpdateDeliveryZoneRequestDTO } from '../../types/dtos/update-delivery-zone.dto';
import { ADMIN_DELIVERY_ZONES_APP_PATHS } from '../../constants/delivery-zones-app-paths.constants';
import { DELIVERY_ZONE_CONFIRM_DIALOG_CONTENT } from '../../constants/delivery-zone-details.constants';
import { DeliveryZoneInformationsTab } from '../../components/Containers/DeliveryZoneInformationsTab.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import { NotFoundState } from '../../../shared/components/States/NotFoundState.component';

type DialogState = { kind: 'none' } | { kind: 'confirm'; action: 'archive' | 'restore' };

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '1rem',
  },
});

export default function AdminDeliveryZoneDetailsView() {
  const classes = useStyles();
  const { deliveryZoneId } = useParams<{ deliveryZoneId: string }>();

  const { isLoading, deliveryZone, refetch } = useAdminGetDeliveryZoneById({ deliveryZoneId });
  const { updateDeliveryZone, isSubmitting: isUpdating } = useUpdateDeliveryZone();
  const { archiveDeliveryZone, isSubmitting: isArchiving } = useArchiveDeliveryZone();
  const { restoreDeliveryZone, isSubmitting: isRestoring } = useRestoreDeliveryZone();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleDeliveryZoneUpdated = async (data: UpdateDeliveryZoneRequestDTO): Promise<DeliveryZone | null> => {
    if (isLoading || !deliveryZone) return null;

    const updatedDeliveryZone = await updateDeliveryZone({ deliveryZoneId: deliveryZone.id, deliveryZoneData: data });
    if (!updatedDeliveryZone) return null;

    await refetch();
    return updatedDeliveryZone;
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm' || !deliveryZone) return;

    const result =
      dialog.action === 'archive'
        ? await archiveDeliveryZone(deliveryZone.id)
        : await restoreDeliveryZone(deliveryZone.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  return (
    <section className={classes.root}>
      {isLoading && <Skeleton variant="rect" animation="wave" height={92} />}
      {!isLoading && !deliveryZone && (
        <NotFoundState
          title="Zone de livraison introuvable"
          backLabel="Retour aux zones"
          backTo={ADMIN_DELIVERY_ZONES_APP_PATHS.DELIVERY_ZONES_LIST}
        />
      )}
      {!isLoading && deliveryZone && (
        <DeliveryZoneInformationsTab
          deliveryZone={deliveryZone}
          isUpdating={isUpdating}
          updateDeliveryZone={handleDeliveryZoneUpdated}
          onArchiveActionClick={() =>
            setDialog({ kind: 'confirm', action: deliveryZone.archivedAt !== null ? 'restore' : 'archive' })
          }
        />
      )}
      {dialog.kind === 'confirm' && deliveryZone && (
        <ConfirmActionDialog
          open
          title={DELIVERY_ZONE_CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={DELIVERY_ZONE_CONFIRM_DIALOG_CONTENT[dialog.action].description(deliveryZone.name)}
          confirmLabel={DELIVERY_ZONE_CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isArchiving || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
